import { useIsFocused } from "expo-router/react-navigation";
import { router } from 'expo-router';
import { useLayoutEffect, useState } from 'react';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import NpoImpactStudyForm from '@/components/forms/NpoImpactStudyForm';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { ImpactStudyData } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { safeJsonParse } from '@/utils/jsonParse';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const ImpactStudy = () => {
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) setCurrentPage(9);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');
  const initialData = {
    impactStudy: typeof nonProfitOrg?.impactStudy === 'string' ? safeJsonParseArray(nonProfitOrg.impactStudy)[0] : '',
    impactStudyDeny: nonProfitOrg?.impactStudyDeny
  };

  const updateNonProfit = ({
    impactStudy,
    impactStudyDeny
  }: {
    impactStudy: string[] | null;
    impactStudyDeny: boolean;
  }) => {
    nonProfitUpdate(
      {
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        impactStudy: impactStudy,
        impactStudyDeny: impactStudyDeny
      },
      {
        onSuccess: () => {
          onBoardingUpdate(
            {
              preferenceId: authState.preferenceId ?? '',
              impactStudy: true
            },
            {
              onSuccess: () => {
                setIsLoading(false);
                setFileProgress(undefined);
                router.navigate('/(app)/login/nonProfit/onBoarding/salaries');
              }
            }
          );
        },
        onError: (e) => {
          setIsLoading(false);
          console.error(e);
        }
      }
    );
  };

  const onSubmit = async (data: ImpactStudyData) => {
    try {
      setIsLoading(true);
      if (data.impactStudy && !containsSection(data.impactStudy, 'impactStudy')) {
        let fileToUpload: FileToUpload = {
          uri: data.impactStudy,
          name: `impact_study.${new Date().toISOString()}`
        };
        await uploadToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/impactStudy/`,
          fileToUpload,
          (progress) => setFileProgress(progress)
        ).then((storageFile) => {
          updateNonProfit({
            impactStudy: [storageFile.metadata.fullPath],
            impactStudyDeny: data.impactStudyDeny
          });
        });
      } else {
        if (data.impactStudyDeny) {
          updateNonProfit({
            impactStudy: null,
            impactStudyDeny: data.impactStudyDeny
          });
        } else {
          updateNonProfit({
            impactStudy: [],
            impactStudyDeny: data.impactStudyDeny
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <NpoImpactStudyForm
      initialData={initialData}
      isLoading={isLoading || onBoardingIsPending || nonProfitIsPending}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default ImpactStudy;
